import { Component, ElementRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { forkJoin } from 'rxjs';
import { first, map, tap } from 'rxjs/operators';

import { ImageUploaderOutput, toAppwriteStorageBucketId } from '@prism/prism-shared';
import { AppwriteStorageService } from '../../services/appwrite-storage-compat.service';
import { resizeImage } from './resize-image.util';

@Component({
  selector: 'prism-image-uploader',
  templateUrl: './image-uploader.component.html',
  styleUrls: ['./image-uploader.component.scss']
})
export class ImageUploaderComponent implements OnInit {

  public imageUploading: boolean = false;

  @Input() newFilePath!: string;
  @Input() newFileName!: string;
  @Input() existingImageUrl: string | null = ''
  @Input() maxImageSize: number = 400;
  @Input() thumbnailSize: number = 100;
  @Input() imageInputClass: string = '';
  @Input() loadingImageClass: string = '';
  @Input() disabled = false;
  @Input() compact = false;

  @Input() bucketId: string = '';

  @Output() output: EventEmitter<ImageUploaderOutput> = new EventEmitter();

  constructor(
    private elementRef: ElementRef,
    private storage: AppwriteStorageService,
  ) { }

  ngOnInit(): void {
    if (!this.newFilePath) {
      throw Error('newFilePath needs to be set')
    }
    this.bucketId = toAppwriteStorageBucketId(this.bucketId || this.newFilePath);
  }


  public openFileExplorer(): void {
    const fileInput = this.elementRef.nativeElement.querySelector('#upload') as HTMLInputElement;
    fileInput.click();
  }

  public async pasteImage(event: Event) {
    event.stopPropagation();

    const results = (await navigator.clipboard.read())[0]
    if (results.types.includes('image/png')) {
      const blob = await results.getType('image/png')
      await this.uploadImage(blob as File)
    }
  }

  public async uploadImageFromInput(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) { return; }
    const blob = await resizeImage(input.files[0]);
    await this.uploadImage(blob as File)
  }

  public async uploadImage(blob: File) {
    this.imageUploading = true;

    const thumbnailImageBlob = await resizeImage(blob, this.thumbnailSize)
    const imageBlob = await resizeImage(blob, this.maxImageSize);

    const fullId = this.storage.createId();
    const thumbId = this.storage.createId();
    const fullName = this.newFileName ? `${this.newFileName}.jpg` : `${fullId}.jpg`;
    const thumbName = this.newFileName ? `${this.newFileName}_thumb.jpg` : `${thumbId}.jpg`;

    forkJoin([
      this.storage.upload$(this.bucketId, fullId, imageBlob, fullName),
      this.storage.upload$(this.bucketId, thumbId, thumbnailImageBlob, thumbName),
    ]).pipe(
      first(),
      map(([fullResp, thumbResp]) => ({
        imageUrl: this.storage.getFileViewUrl(this.bucketId, fullResp.id),
        thumbnailImageUrl: this.storage.getFileViewUrl(this.bucketId, thumbResp.id),
      })),
      tap((urls) => {
        this.existingImageUrl = urls.imageUrl;
        this.imageUploading = false;
        const output: ImageUploaderOutput = urls;
        this.output.emit(output)
      })
    ).subscribe()
  }
}
